import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common';
import { SourceFormatSchema } from './publication';

/**
 * Feltöltés és konverzió életciklus.
 * Az állapotgép: queued → uploading → uploaded → scanning → validating →
 *                converting → optimizing → indexing → completed | failed
 */
export const IngestionStatusSchema = z.enum([
  'queued',
  'uploading',
  'uploaded',
  'scanning',
  'validating',
  'converting',
  'optimizing',
  'indexing',
  'completed',
  'failed',
  'cancelled',
]);
export type IngestionStatus = z.infer<typeof IngestionStatusSchema>;

export const PresignedUploadRequestSchema = z.object({
  filename: z.string().min(1).max(500),
  contentType: z.string().min(3).max(200),
  bytes: z.number().int().positive().max(1_000_000_000), // 1 GB hard cap
  detectedFormat: SourceFormatSchema.optional(),
});
export type PresignedUploadRequest = z.infer<typeof PresignedUploadRequestSchema>;

export const PresignedUploadResponseSchema = z.object({
  uploadId: UuidSchema,
  url: z.string().url(),
  method: z.enum(['PUT', 'POST']),
  headers: z.record(z.string(), z.string()),
  expiresAt: IsoDateTimeSchema,
});
export type PresignedUploadResponse = z.infer<typeof PresignedUploadResponseSchema>;

export const IngestionJobSchema = z.object({
  id: UuidSchema,
  publicationId: UuidSchema.nullable(),
  status: IngestionStatusSchema,
  filename: z.string(),
  sourceFormat: SourceFormatSchema.nullable(),
  bytes: z.number().int().nonnegative(),
  progressPercent: z.number().min(0).max(100),
  message: z.string().nullable(),
  error: z
    .object({
      // Kötelező: gépi olvasású azonosító. Egyebek opcionálisak — a backend
      // konkrét hiba-családtól függően extra mezőket is rakhat (pl. expected/actual).
      code: z.string(),
      message: z.string().optional(),
      detail: z.string().optional(),
    })
    .passthrough()
    .nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  completedAt: IsoDateTimeSchema.nullable(),
});
export type IngestionJob = z.infer<typeof IngestionJobSchema>;

/**
 * A konverziós sor adminoldali lista-szűrői.
 * A backend-en az ingestion-svc-en megy: `GET /jobs?status=&search=&from=&to=`
 */
export const IngestionJobListQuerySchema = z.object({
  cursor: z.string().nullable().optional(),
  limit: z.number().int().min(1).max(100).default(25),
  status: IngestionStatusSchema.optional(),
  search: z.string().max(200).optional(),
  /** ISO datetime-ig (`createdAt >= from`) */
  from: IsoDateTimeSchema.optional(),
  /** ISO datetime-tól (`createdAt <= to`) */
  to: IsoDateTimeSchema.optional(),
});
export type IngestionJobListQuery = z.infer<typeof IngestionJobListQuerySchema>;

/**
 * Egy ingest job életciklusa során rögzített események — a `/uploads/[id]` lapon
 * idővonal-szerűen jelenítjük meg, az ingestion-svc bus-eseményeiből származik.
 */
export const IngestionJobEventSchema = z.object({
  id: UuidSchema,
  jobId: UuidSchema,
  status: IngestionStatusSchema,
  message: z.string().nullable(),
  occurredAt: IsoDateTimeSchema,
  /** Opcionális részletek (JSON), pl. stage, container hash, error detail */
  detail: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type IngestionJobEvent = z.infer<typeof IngestionJobEventSchema>;

export const ValidationReportSchema = z.object({
  valid: z.boolean(),
  epubcheckVersion: z.string(),
  messages: z.array(
    z.object({
      severity: z.enum(['fatal', 'error', 'warning', 'info', 'usage', 'suppressed']),
      id: z.string(),
      message: z.string(),
      location: z
        .object({
          path: z.string().optional(),
          line: z.number().int().optional(),
          column: z.number().int().optional(),
        })
        .optional(),
    }),
  ),
  counts: z.object({
    fatal: z.number().int().nonnegative(),
    error: z.number().int().nonnegative(),
    warning: z.number().int().nonnegative(),
    info: z.number().int().nonnegative(),
  }),
});
export type ValidationReport = z.infer<typeof ValidationReportSchema>;
